/**
 * Edge keys over a {@link Space} — the canonical identity of the boundary
 * *between* two adjacent cells. A thin wall lives on a shared edge, not in a
 * cell: two units stand on either side of it. The edge has **no inherent
 * orientation** — the boundary `A|B` is the same boundary as `B|A` — so every
 * lookup must collapse to one canonical key regardless of which cell you
 * approach from. Get this wrong and a wall blocks from one side only.
 *
 * **The canonicalizer.** The unordered pair `{A, B}` keyed by *sorting the two
 * cells' own keys* and joining them with a separator: `edgeKey(a, b) ===
 * edgeKey(b, a)` for all adjacent `a`, `b`, in every direction. Sorting the
 * already-canonical `space.key(cell)` strings means we lean on the space's own
 * serialization (`"q,r"` for hex) rather than inventing a `(min-cell, direction)`
 * scheme — grid-agnostic, and there's exactly one key per physical edge. The `|`
 * separator can't appear in a `"q,r"` cell key, so the split is unambiguous.
 *
 * **The two properties this leans on are `Space<P>` contract law** (see
 * `space.ts`'s `key` doc): a conforming `key` never contains `|`, and `<=` on
 * keys sorts stably. `runSpaceContract` asserts both for every impl, so this
 * stays one grid-agnostic canonicalizer rather than a per-grid `edgeKey` seam.
 *
 * Edges are only meaningful between **adjacent** cells (they share a boundary);
 * `edgeKey` of two non-adjacent cells is a string but names no real edge, and the
 * encounter layer only ever stores/queries keys built from adjacent pairs (the
 * `line()` spine's consecutive cells, a cell + a neighbor).
 */

import type { Space } from "./space";

/** The separator between the two cell keys in an edge key. Absent from `"q,r"` keys. */
const SEP = "|";

/**
 * The **canonical** key for the edge shared by adjacent cells `a` and `b` —
 * order-independent (`edgeKey(a, b) === edgeKey(b, a)`). Built by sorting the two
 * cells' own `space.key`s, so it's grid-agnostic and there's exactly one key per
 * physical edge. Only meaningful for *adjacent* `a`, `b`.
 */
export function edgeKey<P>(space: Space<P>, a: P, b: P): string {
  const ka = space.key(a);
  const kb = space.key(b);
  return ka <= kb ? `${ka}${SEP}${kb}` : `${kb}${SEP}${ka}`;
}

/** Split an edge key back into its two cell keys (canonical order). */
export function parseEdgeKey(k: string): [string, string] {
  const i = k.indexOf(SEP);
  if (i < 0) throw new Error(`invalid edge key: ${JSON.stringify(k)}`);
  return [k.slice(0, i), k.slice(i + SEP.length)];
}
